/*
  This file contains some utility functions and Flow types for the CSS subset
  that is supported by Webflow.

  Initially its usage it's limited to the atoms `style` property, but could be helpful
  in other parts in the future.
*/

// @ts-expect-error - TS2305 - Module '"tinycolor2"' has no exported member 'Rgba'.
import {type Rgba} from 'tinycolor2';

type Auto = 'auto';
type Default = 'default';
type None = 'none';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Percentage = string;
export const percent = (n: number): Percentage => `${n.toString()}%`;

export type WebflowCssProperty =
  | 'outline-color'
  | 'outline-offset'
  | 'outline-style'
  | 'outline-width'
  | 'position'
  | 'left'
  | 'top'
  | 'right'
  | 'bottom'
  | 'z-index'
  | 'object-fit'
  | 'object-position'
  | 'display'
  | 'visibility'
  | 'overflow'
  | 'overflow-x'
  | 'overflow-y'
  | 'box-sizing'
  | 'width'
  | 'height'
  | 'max-height'
  | 'max-width'
  | 'min-height'
  | 'min-width'
  | 'margin'
  | 'margin-top'
  | 'margin-right'
  | 'margin-bottom'
  | 'margin-left'
  | 'padding'
  | 'padding-top'
  | 'padding-right'
  | 'padding-bottom'
  | 'padding-left'
  | 'float'
  | 'clear'
  | 'flex-direction'
  | 'justify-content'
  | 'grid-auto-flow'
  | 'grid-auto-columns'
  | 'grid-auto-rows'
  | 'grid-column-gap'
  | 'grid-row-gap'
  | 'grid-column-start'
  | 'grid-row-start'
  | 'grid-column-end'
  | 'grid-row-end'
  | 'grid-area'
  | 'grid-template-areas'
  | 'grid-template-columns'
  | 'grid-template-rows'
  | 'justify-items'
  | 'flex-wrap'
  | 'align-items'
  | 'align-content'
  | 'align-self'
  | 'justify-self'
  | 'order'
  | 'flex-grow'
  | 'flex-shrink'
  | 'flex-basis'
  | 'flex-flow'
  | 'flex'
  | 'border'
  | 'border-style'
  | 'border-width'
  | 'border-color'
  | 'border-top'
  | 'border-right'
  | 'border-bottom'
  | 'border-left'
  | 'border-top-style'
  | 'border-top-width'
  | 'border-top-color'
  | 'border-right-style'
  | 'border-right-width'
  | 'border-right-color'
  | 'border-bottom-style'
  | 'border-bottom-width'
  | 'border-bottom-color'
  | 'border-left-style'
  | 'border-left-width'
  | 'border-left-color'
  | 'border-radius'
  | 'border-top-left-radius'
  | 'border-top-right-radius'
  | 'border-bottom-left-radius'
  | 'border-bottom-right-radius'
  | 'backdrop-filter'
  | 'background-clip'
  | 'background'
  | 'background-color'
  | 'background-image'
  | 'background-position'
  | 'background-position-x'
  | 'background-position-y'
  | 'background-size'
  | 'background-repeat'
  | 'background-repeat-x'
  | 'background-repeat-y'
  | 'background-attachment'
  | 'box-shadow'
  | 'opacity'
  | 'filter'
  | 'backface-visibility'
  | 'perspective'
  | 'perspective-origin'
  | 'pointer-events'
  | 'transform'
  | 'transform-origin'
  | 'transform-style'
  | 'transition'
  | 'transition-property'
  | 'transition-duration'
  | 'transition-timing-function'
  | 'transition-delay'
  | 'direction'
  | 'font-family'
  | 'color'
  | 'font-size'
  | 'line-height'
  | 'font-style'
  | 'font-variant'
  | 'font-weight'
  | 'text-align'
  | 'text-overflow'
  | 'letter-spacing'
  | 'text-decoration'
  | 'text-indent'
  | 'text-transform'
  | 'list-style-type'
  | 'text-shadow'
  | 'font-smoothing'
  | 'column-count'
  | 'column-gap'
  | 'column-rule'
  | 'column-rule-color'
  | 'column-rule-style'
  | 'column-rule-width'
  | 'column-span'
  | 'vertical-align'
  | 'cursor'
  | 'white-space'
  | 'white-space-collapse'
  | 'word-break'
  | 'word-spacing'
  | 'word-wrap'
  | 'overflow-wrap'
  | 'text-wrap-mode'
  | 'text-wrap-style'
  | 'hyphens'
  | 'aspect-ratio'
  | 'mix-blend-mode'
  | 'font-variation-settings'
  | '-webkit-text-stroke-width'
  | '-webkit-text-stroke-color'
  | 'text-underline-offset'
  | 'text-decoration-thickness'
  | 'text-decoration-style'
  | 'text-decoration-color'
  | 'text-decoration-skip-ink'
  | 'text-underline-position';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
// We don't keep a "Length" type, as some of Webflow properties are compatible with `em` and `px`, others only with `px` etc.
type Pixels = string;
export const px = (n: number): Pixels => `${Math.round(n).toString()}px`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Em = string;
export const em = (n: number): Em => `${n.toString()}em`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Rem = string;
export const rem = (n: number): Rem => `${n.toString()}rem`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Vh = string;
export const vh = (n: number): Vh => `${n.toString()}vh`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Vw = string;
export const vw = (n: number): Vw => `${n.toString()}vw`;

/* Transform */
// Webflow uses a custom system of transform "parts" that can be deterministically
// parsed from the style block `styleLess` property. These types just conform to these constraints.
type TranslateUnit = Pixels | Percentage | Em | Rem | Vh | Vw;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Deg = string;
export const deg = (n: number): Deg => `${Math.round(n).toString()}deg`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Rotate = string;
export const rotate = (degZ: Deg): Rotate => `rotate(${degZ})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type RotateXYZ = string;
export const rotateXYZ = (degX: Deg, degY: Deg, degZ: Deg): RotateXYZ =>
  `rotateX(${degX}) rotateY(${degY}) rotateZ(${degZ})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Scale = string;
export const scale = (scaleX: number): Scale => `scale(${scaleX})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Scale3d = string;
export const scale3d = (
  scaleX: number,
  scaleY: number,
  scaleZ: number
): Scale3d => `scale(${scaleX}, ${scaleY}, ${scaleZ})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Translate = string;
export const translate = (
  translateX: TranslateUnit,
  translateY: TranslateUnit
): Translate => `translate(${translateX}, ${translateY})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Translate3d = string;
export const translate3d = (
  translateX: TranslateUnit,
  translateY: TranslateUnit,
  translateZ: TranslateUnit
): Translate3d => `translate3d(${translateX}, ${translateY}, ${translateZ})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Skew = string;
export const skew = (skewX: Deg, skewY: Deg): Skew =>
  `skew(${skewX}, ${skewY})`;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Perspective = string;
export const perspective = (perspectiveValue: Pixels): Perspective =>
  `perspective(${perspectiveValue})`;
type _TransformPart =
  | Rotate
  | RotateXYZ
  | Scale
  | Scale3d
  | Translate
  | Translate3d
  | Skew
  | Perspective;

/* Transition */
type TransitionPropertyPart = string; // Consider inlining all of CSS_ANIMATABLE_PROPS here?
type TransitionProperty = Array<TransitionPropertyPart>;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/* Filter */
type Blur = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type HueRotate = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Saturation = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Brightness = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Contrast = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Grayscale = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Sepia = string;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Invert = string;
type DropShadow = string;

type FilterPart =
  | Blur
  | HueRotate
  | Saturation
  | Brightness
  | Contrast
  | Grayscale
  | Sepia
  | Invert
  | DropShadow;
type Filter = Array<FilterPart>;

type _Cursor =
  | 'pointer'
  | 'not-allowed'
  | 'wait'
  | 'progress'
  | 'help'
  | 'context-menu'
  | 'cell'
  | 'crosshair'
  | 'text'
  | 'vertical-text'
  | 'grab'
  | 'grabbing'
  | 'alias'
  | 'copy'
  | 'move'
  | 'zoom-in'
  | 'zoom-out'
  | 'col-resize'
  | 'row-resize'
  | 'nesw-resize'
  | 'nwse-resize'
  | 'ew-resize'
  | 'ns-resize'
  | 'n-resize'
  | 'w-resize'
  | 's-resize'
  | 'e-resize'
  | 'nw-resize'
  | 'ne-resize'
  | 'sw-resize'
  | 'se-resize';
type _Display =
  | 'block'
  | 'inline-block'
  | 'inline'
  | 'flex'
  | 'grid'
  | 'table'
  | 'none'
  | 'inline-flex'
  | 'inline-grid';
type _LineStyle = 'solid' | 'dashed' | 'dotted';

/* Typography */
type _FontFamily = string;
type _FontStyle = 'normal' | 'italic';
type _FontVariationSettings = string;
type _TextDecoration = 'underline' | 'line-through' | 'overline';
type _TextColumnCount = number;
type _TextShadow = string;
type _TextStrokeWidth = Pixels | Em | Percentage;
type _TextStrokeColor = Rgba;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type _BoxShadow = string;
export const boxShadow = (boxShadowInput: {
  xOffset: Pixels;
  yOffset: Pixels;
  blurRadius: Pixels;
  spreadRadius: Pixels;
  color: RgbaColor;
}): _BoxShadow =>
  `${boxShadowInput.xOffset} ${boxShadowInput.yOffset} ${
    boxShadowInput.blurRadius
  } ${boxShadowInput.spreadRadius} ${colorToString(boxShadowInput.color)}`;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type Url = string;
export const url = (path: string): Url => `url(${path})`;
export type BackgroundRepeat = 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat';

type GlobalValues = 'inherit' | 'initial' | 'unset';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type TwoValueSize = string;
export const twoValueSize = (width: Size, height: Size): TwoValueSize =>
  `${width} ${height}`;

type BackgroundSize = 'cover' | 'contain' | GlobalValues | Size | TwoValueSize;

type BackgroundPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'center'
  | GlobalValues
  | TwoValueSize;

/* -------------- */
/* Exported types */
/* -------------- */

// Mostly taken from designer-flux/utils/CssPropertyUtils.js unless specified otherwise:

// From AbsoluteEditor.jsx
export type AbsoluteLayout = Pixels | Percentage | Auto;
// From designer-flux/constants/FlexProps.jsx
export type AlignContent =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'stretch'
  | 'space-between'
  | 'space-around';
export type AlignItems =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'stretch'
  | 'baseline';
export type Appearance = 'none';
export type BorderRadius = Pixels | Percentage;
export type BorderWidth = Pixels | Percentage;
// From designer-flux/components/ShadowsSection.jsx
export type BoxShadow = _BoxShadow;
export type TextShadow = _TextShadow;
// From designer-flux/components/StylePanelPositionSection.jsx
export type Clear = 'none' | 'left' | 'right' | 'both';
export type RgbaColor = Rgba;
// From designer-flux/components/KitCursorInput.jsx
export type Cursor = _Cursor | Auto | Default | None;
// From designer-flux/components/StylePanel/LayoutSection/Display/DisplaySettings.jsx
export type Display = _Display | None;
export type Visibility = 'visible' | 'hidden';
// From designer-flux/components/FlexItemLayout.jsx
export type FlexBasis = Pixels | Percentage | Auto;
// From designer-flux/constants/FlexProps.jsx
export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';
// From designer-flux/constants/FlexProps.jsx
export type FlexWrap = 'wrap' | 'nowrap' | 'wrap-reverse';
// From designer-flux/components/StylePanelPositionSection.jsx
export type Float = 'none' | 'left' | 'right';
// From designer-flux/constants/FlexProps.jsx
export type JustifyContent =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'space-between'
  | 'space-around';
// From designer-flux/components/BorderSideEditor.jsx
export type LineStyle = _LineStyle | None;
export type MarginWidth = Pixels | Percentage | Em | Auto;
export type Overflow = 'visible' | 'hidden' | 'scroll' | 'auto' | 'clip';
export type PaddingWidth = Pixels | Percentage | Em | Vh | Vw;
// From designer-flux/components/LayoutSection.jsx
export type PointerEvents = 'none' | 'auto';
export type Position = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
export type Size = Pixels | Percentage | Vh | Auto | None;
export type TextAlign =
  | 'left'
  | 'right'
  | 'center'
  | 'justify'
  | 'start'
  | 'end';
// From designer-flux/components/TypographySection.jsx
export type TextDecoration = _TextDecoration | None;
export type TextDirection = 'ltr' | 'rtl';
type TextIndent = Pixels | Em | Percentage;
export type TextTransform = 'none' | 'uppercase' | 'capitalize' | 'lowercase';
export type LetterSpacing = Pixels | Em | Percentage;
export type TextColumnCount = _TextColumnCount;
export type TextStrokeWidth = _TextStrokeWidth;
export type TextStrokeColor = _TextStrokeColor;
export type TextOverflow = 'clip' | 'ellipsis';

export type BoxSizing = 'content-box' | 'border-box';

// From designer-flux/utils/StyleStoreFormatter.formatTransformParts and designer-flux/parsers/TransformParser
export type Transform = Array<_TransformPart>;
// From designer-flux/components/TypographySection.jsx
export type TypographyFamily = _FontFamily;
export type TypographyVariationSettings = _FontVariationSettings;
export type TypographyStyle = _FontStyle;
export type TypographySize = Pixels | Em | Percentage;
export type TypographyLineHeight = Pixels | Em | Percentage | 'normal';
export type VerticalAlign =
  | 'baseline'
  | 'sub'
  | 'super'
  | 'top'
  | 'text-top'
  | 'middle'
  | 'bottom'
  | 'text-bottom';

export type TypographyWeight =
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900;

// Originally from designer-flux/components/ui/StylePanelGridItemSection.jsx
export type BoxItemAlignment =
  | 'start'
  | 'end'
  | 'center'
  | 'stretch'
  | 'auto'
  | 'normal'
  | 'baseline';

export type WhiteSpaceStyle =
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap'
  | 'break-spaces'
  | 'initial'
  | 'inherit';

export type TextWrapMode = 'wrap' | 'nowrap';

export type WhiteSpaceCollapse =
  | 'collapse'
  | 'preserve'
  | 'preserve-breaks'
  | 'preserve-spaces'
  | 'break-spaces';

export type TextWrapStyle = 'auto' | 'balance' | 'pretty' | 'stable';

export type HyphensStyle = 'none' | 'manual' | 'auto';

export type AspectRatio = string;
export type WordBreakStyle = 'normal' | 'break-all' | 'keep-all';
export type OverflowWrapStyle = 'normal' | 'anywhere' | 'break-word';
export type TextDecorationThickness =
  | Pixels
  | Em
  | Percentage
  | 'auto'
  | 'inherit';
export type TextDecorationStyle =
  | 'solid'
  | 'double'
  | 'dotted'
  | 'dashed'
  | 'wavy';
export type TextDecorationColor = RgbaColor;
export type TextDecorationSkipInk = 'auto' | 'none';
export type TextUnderlinePosition = 'auto' | 'under';
export type TextUnderlineOffsetValue =
  | Pixels
  | Em
  | Percentage
  | 'auto'
  | 'inherit';

export type OutlineColor = RgbaColor;
export type OutlineOffset = Pixels | Em;
export type OutlineStyle =
  | 'auto'
  | 'none'
  | 'dotted'
  | 'dashed'
  | 'solid'
  | 'double'
  | 'ridge'
  | 'inset'
  | 'outset'
  | 'double'
  | 'inherit'
  | 'initial'
  | 'unset'
  | 'groove';
export type OutlineWidth = Pixels | Em | 'thin' | 'medium' | 'thick';

export type GridItemOrder = number;
export type GridArea = null | string;
export type GridStartEnd = null | number | string;
export type GridTemplate = null | string;
export type GridGap = null | string;
export type ListStyleType =
  | 'none'
  | 'disc'
  | 'square'
  | 'decimal'
  | 'lower-alpha'
  | 'lower-roman';

export type BackgroundClipType =
  | 'border-box'
  | 'padding-box'
  | 'content-box'
  | 'text';

export type ObjectFit = 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';

export type MixBlendMode =
  | 'normal'
  | 'darken'
  | 'multiply'
  | 'color-burn'
  | 'lighten'
  | 'screen'
  | 'color-dodge'
  | 'overlay'
  | 'soft-light'
  | 'hard-light'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity';

export type WebflowCssProperties = {
  alignContent?: AlignContent;
  alignItems?: AlignItems;
  alignSelf?: BoxItemAlignment;
  appearance?: Appearance;
  backdropFilter?: Filter;
  backgroundClip?: BackgroundClipType;
  backgroundColor?: RgbaColor;
  backgroundSize?: BackgroundSize;
  backgroundPosition?: BackgroundPosition;
  backgroundImage?: Url | None;
  backgroundRepeat?: BackgroundRepeat;
  borderBottomLeftRadius?: BorderRadius;
  borderBottomRightRadius?: BorderRadius;
  borderTopLeftRadius?: BorderRadius;
  borderTopRightRadius?: BorderRadius;
  borderBottomWidth?: BorderWidth;
  borderLeftWidth?: BorderWidth;
  borderRightWidth?: BorderWidth;
  borderTopWidth?: BorderWidth;
  borderBottomColor?: RgbaColor;
  borderLeftColor?: RgbaColor;
  borderRightColor?: RgbaColor;
  borderTopColor?: RgbaColor;
  borderBottomStyle?: LineStyle;
  borderLeftStyle?: LineStyle;
  borderRightStyle?: LineStyle;
  borderTopStyle?: LineStyle;
  bottom?: AbsoluteLayout;
  boxShadow?: BoxShadow;
  boxSizing?: BoxSizing;
  mixBlendMode?: MixBlendMode;
  clear?: Clear;
  color?: RgbaColor;
  columnCount?: TextColumnCount;
  content?: string;
  cursor?: Cursor;
  direction?: TextDirection;
  display?: Display;
  filter?: Filter;
  flexBasis?: FlexBasis;
  flexDirection?: FlexDirection;
  flexGrow?: number;
  flexShrink?: number;
  flexWrap?: FlexWrap;
  float?: Float;
  fontFamily?: TypographyFamily;
  fontSize?: TypographySize;
  fontStyle?: TypographyStyle;
  fontVariationSettings?: TypographyVariationSettings;
  fontWeight?: TypographyWeight;
  gridArea?: GridArea;
  gridColumnStart?: GridStartEnd;
  gridColumnEnd?: GridStartEnd;
  gridRowStart?: GridStartEnd;
  gridRowEnd?: GridStartEnd;
  gridTemplateColumns?: GridTemplate;
  gridTemplateRows?: GridTemplate;
  gridAutoColumns?: GridTemplate;
  gridAutoRows?: GridTemplate;
  gridRowGap?: GridGap;
  gridColumnGap?: GridGap;
  height?: Size;
  justifyContent?: JustifyContent;
  justifyItems?: BoxItemAlignment;
  justifySelf?: BoxItemAlignment;
  left?: AbsoluteLayout;
  lineHeight?: TypographyLineHeight;
  letterSpacing?: LetterSpacing;
  marginBottom?: MarginWidth;
  marginLeft?: MarginWidth;
  marginRight?: MarginWidth;
  marginTop?: MarginWidth;
  maxHeight?: Size;
  maxWidth?: Size;
  minHeight?: Size;
  minWidth?: Size;
  objectFit?: ObjectFit;
  objectPosition?: TwoValueSize;
  opacity?: number;
  overflow?: Overflow;
  order?: number;
  outlineColor?: OutlineColor;
  outlineOffset?: OutlineOffset;
  outlineStyle?: OutlineStyle;
  outlineWidth?: OutlineWidth;
  paddingBottom?: PaddingWidth;
  paddingLeft?: PaddingWidth;
  paddingRight?: PaddingWidth;
  paddingTop?: PaddingWidth;
  pointerEvents?: PointerEvents;
  position?: Position;
  right?: AbsoluteLayout;
  textAlign?: TextAlign;
  textDecoration?: TextDecoration;
  textOverflow?: TextOverflow;
  textIndent?: TextIndent;
  textTransform?: TextTransform;
  textShadow?: TextShadow;
  textStrokeColor?: TextStrokeColor;
  textStrokeWidth?: TextStrokeWidth;
  textUnderlineOffset?: TextUnderlineOffsetValue;
  textDecorationThickness?: TextDecorationThickness;
  textDecorationStyle?: TextDecorationStyle;
  textDecorationColor?: TextDecorationColor;
  textDecorationSkipInk?: TextDecorationSkipInk;
  textUnderlinePosition?: TextUnderlinePosition;
  transform?: Transform;
  transition?: TransitionProperty;
  top?: AbsoluteLayout;
  aspectRatio?: AspectRatio;
  verticalAlign?: VerticalAlign;
  whiteSpace?: WhiteSpaceStyle;
  whiteSpaceCollapse?: WhiteSpaceCollapse;
  width?: Size;
  wordBreak?: WordBreakStyle;
  overflowWrap?: OverflowWrapStyle;
  textWrapMode?: TextWrapMode;
  textWrapStyle?: TextWrapStyle;
  hyphens?: HyphensStyle;
  zIndex?: Auto | number;
  listStyleType?: ListStyleType;
  WebkitOverflowScrolling?: Auto | 'touch';
  visibility?: Visibility;
};

const Unknown = 'unknown';
const printUndefined = (key: string) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(
      `A style property with value 'undefined' was passed to convertStyleValueToString: ${key}`
    );
  }
};

export const colorToString = (color: RgbaColor): string =>
  `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;

export const convertStyleValueToString = (
  properties: WebflowCssProperties,
  key: keyof WebflowCssProperties
): string => {
  switch (key) {
    case 'backgroundColor':
    case 'borderBottomColor':
    case 'borderLeftColor':
    case 'borderRightColor':
    case 'borderTopColor':
    case 'color':
    case 'outlineColor': {
      const color = properties[key];
      if (!color) {
        printUndefined(key);
        return Unknown;
      }
      return colorToString(color);
    }

    case 'alignContent':
    case 'alignItems':
    case 'alignSelf':
    case 'appearance':
    case 'backdropFilter':
    case 'backgroundClip':
    case 'backgroundImage':
    case 'backgroundRepeat':
    case 'backgroundPosition':
    case 'backgroundSize':
    case 'borderBottomLeftRadius':
    case 'borderBottomRightRadius':
    case 'borderTopLeftRadius':
    case 'borderTopRightRadius':
    case 'borderBottomWidth':
    case 'borderLeftWidth':
    case 'borderRightWidth':
    case 'borderTopWidth':
    case 'borderBottomStyle':
    case 'borderLeftStyle':
    case 'borderRightStyle':
    case 'borderTopStyle':
    case 'bottom':
    case 'boxShadow':
    case 'boxSizing':
    case 'clear':
    case 'columnCount':
    case 'content':
    case 'cursor':
    case 'direction':
    case 'display':
    case 'filter':
    case 'flexBasis':
    case 'flexDirection':
    case 'flexGrow':
    case 'flexShrink':
    case 'flexWrap':
    case 'float':
    case 'fontFamily':
    case 'fontSize':
    case 'fontStyle':
    case 'fontVariationSettings':
    case 'fontWeight':
    case 'gridArea':
    case 'gridColumnStart':
    case 'gridColumnEnd':
    case 'gridRowStart':
    case 'gridRowEnd':
    case 'gridRowGap':
    case 'gridColumnGap':
    case 'gridTemplateRows':
    case 'gridTemplateColumns':
    case 'gridAutoRows':
    case 'gridAutoColumns':
    case 'height':
    case 'justifyContent':
    case 'justifyItems':
    case 'justifySelf':
    case 'left':
    case 'lineHeight':
    case 'letterSpacing':
    case 'marginBottom':
    case 'marginLeft':
    case 'marginRight':
    case 'marginTop':
    case 'maxHeight':
    case 'maxWidth':
    case 'minHeight':
    case 'minWidth':
    case 'mixBlendMode':
    case 'objectFit':
    case 'objectPosition':
    case 'opacity':
    case 'overflow':
    case 'order':
    case 'outlineOffset':
    case 'outlineStyle':
    case 'outlineWidth':
    case 'paddingBottom':
    case 'paddingLeft':
    case 'paddingRight':
    case 'paddingTop':
    case 'pointerEvents':
    case 'position':
    case 'right':
    case 'textAlign':
    case 'textDecoration':
    case 'textIndent':
    case 'textShadow':
    case 'textStrokeWidth':
    case 'textStrokeColor':
    case 'textTransform':
    case 'transform':
    case 'transition':
    case 'top':
    case 'whiteSpace':
    case 'whiteSpaceCollapse':
    case 'wordBreak':
    case 'textUnderlineOffset':
    case 'textDecorationThickness':
    case 'textDecorationStyle':
    case 'textDecorationColor':
    case 'textDecorationSkipInk':
    case 'textUnderlinePosition':
    case 'aspectRatio':
    case 'overflowWrap':
    case 'textWrapMode':
    case 'textWrapStyle':
    case 'hyphens':
    case 'width':
    case 'listStyleType':
    case 'zIndex':
    case 'verticalAlign':
    case 'visibility':
    case 'WebkitOverflowScrolling': {
      const t = properties[key];
      // TODO: Review all properties one by one to make sure we're not casting any values that need pre-processing
      return String(t);
    }
    default: {
      /*::
        // Ensure we're handling all possible keys:
        (key: empty);
      */
      if (process.env.NODE_ENV === 'development') {
        console.error(
          `Unknown style property passed to convertStyleValueToString ${key}`
        );
      }
      const t = properties[key];
      return String(t);
    }
  }
};
